import React, { useEffect, useState } from 'react';
import { Form, reduxForm, getFormValues } from 'redux-form';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { Col, Row } from 'react-bootstrap';

import { actions as channelActions } from '../../../channel/redux/channelActions';
import { actions } from '../../redux/brokerActions';
import { Button } from '../../../../common/components';
import {
  EmailContent,
  EmailOverrides,
  LetterContent,
} from '../../../whitelabel/components';
import { InternalButtonBar } from '../../../layout/components';

import getIncludedResource from '../../../../helpers/getIncludedResource';

const BrokerContent = ({
  formValues,
  change,
  handleSubmit,
  actions,
  resource,
  submitting,
  renewalsEnabled,
  brokerId,
}) => {
  const [renewalTemplates, setRenewalTemplates] = useState(null);
  const [policyTemplates, setPolicyTemplates] = useState(null);
  const [renewalLetterTemplates, setRenewalLetterTemplates] = useState(null);

  useEffect(() => {
    handleSetUpBrokerContent(resource);
  }, []);

  useEffect(
    () => {
      handleSetUpBrokerContent(resource);
    },
    [resource],
  );

  const handleSetUpBrokerContent = (broker) => {
    const b2cChannel = getIncludedResource(broker.data, broker.included, 'channel');
    const hasB2Channel = b2cChannel.data.attributes;
    if (hasB2Channel && b2cChannel.data.attributes.metadata.content) {
      change(
        'meta.whitelabel.email_content_override',
        b2cChannel.data.attributes.metadata.email_content_override,
      );
    }

    const contents = hasB2Channel
      ? getIncludedResource(b2cChannel.data, resource.included, 'contents')
      : null;

    getEmailTemplates(contents, b2cChannel);
    getLetterTemplates(contents, b2cChannel);
  };

  const getContents = (templates, contents, resourceName) => {
    return templates.map((template) => {
      const matchedContent = contents
        ? contents.find(
            (content) => content.relationships.template.data.id === template.id,
          )
        : null;
      return matchedContent ? matchedContent : getEmptyContent(resourceName, template);
    });
  };

  const getEmailTemplates = (contents, b2cChannel) => {
    Promise.resolve(actions.getEmailTemplates()).then((emailTemplates) => {
      const resourceName =
        b2cChannel.data.length > 0
          ? b2cChannel.data.attributes.name
          : resource.data.attributes.name;
      setPolicyTemplates(emailTemplates?.policyEmails);
      setRenewalTemplates(emailTemplates?.renewalEmails);

      const policyEmailContent = getContents(
        emailTemplates.policyEmails,
        contents,
        resourceName,
      );
      change('meta.email.policy.content', policyEmailContent);

      if (renewalsEnabled) {
        const renewalEmailContent = getContents(
          emailTemplates.renewalEmails,
          contents,
          resourceName,
        );
        change('meta.email.renewal.content', renewalEmailContent);
      }
    });
  };

  const getLetterTemplates = (contents, b2cChannel) => {
    Promise.resolve(actions.getLetterTemplates()).then((letterTemplates) => {
      const resourceName =
        b2cChannel.data.length > 0
          ? b2cChannel.data.attributes.name
          : resource.data.attributes.name;

      if (renewalsEnabled) {
        setRenewalLetterTemplates(letterTemplates.renewalLetters);
        const renewalLetterContent = getContents(
          letterTemplates.renewalLetters,
          contents,
          resourceName,
        );
        change('meta.letter.renewal.content', renewalLetterContent);
      }
    });
  };

  const getEmptyContent = (name, template) => {
    const sections = Object.keys(template.attributes.sections).reduce(
      (carry, sectionKey) => {
        carry[sectionKey] = '';
        return carry;
      },
      {},
    );

    return {
      attributes: {
        content_type: 'email',
        name: name + ' ' + template.attributes.name,
        sections,
      },
      relationships: {
        template: {
          data: {
            id: template.id,
          },
        },
      },
    };
  };

  const handleContentFormSubmit = (values) => {
    const checkHasContent = (contents) => {
      return contents.reduce((carry, content) => {
        carry = content !== '' ? true : carry;
        return carry;
      }, false);
    };

    if (values.meta.email) {
      values.meta.email.policy.content = values.meta.email.policy.content.reduce(
        (carry, content) => {
          const values = Object.values(content.attributes.sections);
          if (checkHasContent(values)) {
            carry.push(content);
          }
          return carry;
        },
        [],
      );
      values.meta.email.renewal.content = values.meta.email?.renewal?.content.reduce(
        (carry, content) => {
          const values = Object.values(content.attributes.sections);
          if (checkHasContent(values)) {
            carry.push(content);
          }
          return carry;
        },
        [],
      );
    }

    if (values.meta.letter) {
      values.meta.letter.renewal.content = values.meta.letter.renewal.content.reduce(
        (carry, content) => {
          const values = Object.values(content.attributes.sections);
          if (checkHasContent(values)) {
            carry.push(content);
          }
          return carry;
        },
        [],
      );
    }

    return Promise.resolve(actions.createBrokerContent(brokerId, values)).then(() => {
      return actions.getBroker(brokerId);
    });
  };

  if (!formValues) {
    return null;
  }

  return (
    <>
      <>
        <Form onSubmit={handleSubmit(handleContentFormSubmit)}>
          <EmailOverrides />

          <EmailContent
            templates={policyTemplates}
            emails={formValues.meta.email.policy.content}
            emailCategory={'policy'}
          />
          {renewalsEnabled && (
            <EmailContent
              templates={renewalTemplates}
              emailCategory={'renewal'}
              emails={formValues.meta.email?.renewal?.content}
            />
          )}
          <LetterContent
            templates={renewalLetterTemplates}
            letterCategory={'renewal'}
            letters={formValues.meta.letter?.renewal?.content}
          />
          <Row>
            <Col sm={12}>
              <InternalButtonBar
                buttons={[
                  {
                    render: true,
                    permissions: ['broker.edit'],
                    button: (
                      <Button
                        key={'wl'}
                        className="pull-right"
                        type="submit"
                        bsStyle="primary"
                        isLoading={submitting}
                        disabled={submitting}
                        label={'Save Content'}
                      />
                    ),
                  },
                ]}
              />
            </Col>
          </Row>
        </Form>
      </>
    </>
  );
};

const FORM_NAME = 'brokerContentForm';

export default compose(
  connect(
    (state) => {
      return {
        initialValues: {
          meta: {
            email: {
              policy: {
                content: [],
              },
              renewal: {
                content: [],
              },
            },
            letter: {
              renewal: {
                content: [],
              },
            },
          },
        },
        formValues: getFormValues(FORM_NAME)(state),
        isSubmitting: state.broker.isSubmitting,
      };
    },
    (dispatch) => ({
      channelActions: bindActionCreators(channelActions, dispatch),
      actions: bindActionCreators(actions, dispatch),
    }),
  ),
  reduxForm({ form: FORM_NAME }),
)(BrokerContent);
